import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEOHead from "@/components/SEOHead";

/**
 * Pre-launch holding page for /blog.
 *
 * Renders on /blog and on the splat /blog/* so any inbound link to a legacy
 * post URL lands here with HTTP 200 rather than 404 — preserves link equity
 * while the rebuild is deferred to a post-launch wave. noindex,nofollow keeps
 * the placeholder out of search results; sitemap excludes /blog for the same
 * reason. The original BlogPage.jsx + backend /api/blog proxy are left in
 * place for the forward-port.
 */

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

export default function BlogComingSoonPage() {
  return (
    <div data-testid="blog-coming-soon-page">
      <SEOHead
        title="Blog — Coming Soon"
        description="The CanDo Payments blog is being rebuilt and will return shortly. In the meantime, explore our products and resources."
        path="/blog"
        robots="noindex, nofollow"
      />

      {/* Hero */}
      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.p
              variants={fadeUp}
              className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3"
            >
              Coming Soon
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-5"
            >
              Our <span className="gradient-text">blog</span> is being rebuilt
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed">
              We&rsquo;re working on something new. Check back soon for updates, guides, and
              insights from the CanDo Payments team.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-6 py-3 text-sm transition-colors"
            data-testid="blog-coming-soon-home-cta"
          >
            Back to home <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
